/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import type { RenderPolicy } from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import {
	createRelayEnvironment,
	type AtlassianRelayEnvironment,
} from '@atlassian/relay-environment/createRelayEnvironment';
import { getNetworkOptions } from '@jira/platform__graphql-relay-fetch/src/getNetworkOptions.tsx';
import relayLogHandler, {
	requiredFieldLogger,
} from '@jira/platform__graphql-relay-log/src/index.tsx';
import { isStreamingEnabled } from '@jira/ssr__streaming-enabled/src/isStreamingEnabled.tsx';

/**
 * Relay's default `partial` render policy lets a read that is missing data render anyway, with the
 * absent fields resolving to `null`. Under streaming SSR that turns a query which has not arrived by
 * the SSR deadline into a fully laid-out page of empty fields rather than a skeleton, because the
 * store usually holds enough from other queries to satisfy the shallow root selections. `full` makes
 * Relay suspend or fail those reads instead, so the nearest Suspense boundary renders its fallback.
 *
 * Server-only on purpose: `isStreamingEnabled()` is also true in the browser once a streamed page
 * hydrates, and narrowing what the client may render from cache is not the intent.
 *
 * Resolved per read rather than captured once, because streaming is decided per request while this
 * environment is a module singleton. `isStreamingEnabled()` is checked before `fg()` so the gate is
 * only exposed where it can take effect.
 */
const getDefaultRenderPolicy = (): RenderPolicy => {
	if (
		__SERVER__ &&
		(isStreamingEnabled() || fg('jfp-magma-ssr-streaming-cleanup')) &&
		fg('jfp-magma-ssr-relay-full-render-policy')
	) {
		return 'full';
	}
	return 'partial';
};

const sharedRelayEnvironmentConfig = {
	log: relayLogHandler,
	relayFieldLogger: requiredFieldLogger,
	networkOptions: getNetworkOptions(),
	getDefaultRenderPolicy,
};

const platformRelayEnvironment: AtlassianRelayEnvironment = createRelayEnvironment(
	sharedRelayEnvironmentConfig,
);

function getPlatformRelayEnvironment(): AtlassianRelayEnvironment {
	return platformRelayEnvironment;
}

export default getPlatformRelayEnvironment;
